import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useParams} from 'react-router';

export const PREMIUM_LOCALES = ['en', 'hu', 'de'] as const;

export type PremiumLocale = (typeof PREMIUM_LOCALES)[number];

export type PremiumCopy = {
  modal: {
    trigger: string;
    title: string;
    body: string;
    benefits: string[];
    subscribe: string;
  };
  layout: {
    support: string;
    download: string;
    login: string;
    signup: string;
    brandColumn: string;
    premiumSubscription: string;
    appDownload: string;
    contactSupport: string;
    imprint: string;
    company: string;
    communities: string;
    usefulLinks: string;
    plans: string;
    about: string;
    jobs: string;
    news: string;
    forArtists: string;
    developers: string;
    advertising: string;
    investors: string;
    vendors: string;
    webPlayer: string;
    mobileApp: string;
    individual: string;
    duo: string;
    family: string;
    student: string;
    legal: string;
    privacyCenter: string;
    privacyPolicy: string;
    cookies: string;
    aboutAds: string;
    accessibility: string;
    terms: string;
    rights: string;
  };
  premium: {
    eyebrow: string;
    heroTitle: string;
    heroBody: string;
    termsApply: string;
    viewPlans: string;
    tryFree: string;
    perks: {title: string; body: string}[];
    difference: string;
    whatYouGet: string;
    free: string;
    premium: string;
    comparison: string[];
    plansTitle: string;
    plansBody: string;
    plansNote: string;
    premiumTerms: string;
    mostPopular: string;
    perMonth: string;
    getStarted: string;
    termsApplyShort: string;
    questions: string;
    faqs: {q: string; a: string; studentTerms?: boolean}[];
    readStudentTerms: string;
    stillNeedHelp: string;
    visitSupport: string;
    plans: {
      id: string;
      name: string;
      price: string;
      trial: string | null;
      description: string;
      features: string[];
      to: string;
      termsTo: string;
      highlighted: boolean;
    }[];
  };
  support: {
    eyebrow: string;
    title: string;
    searchLabel: string;
    searchPlaceholder: string;
    empty: string;
    back: string;
    articleTitle: string;
    articleBody: string;
    articleMore: string;
    categories: {
      id: string;
      title: string;
      description: string;
      topics: {id: string; label: string}[];
    }[];
  };
};

const en: PremiumCopy = {
  modal: {
    trigger: 'Surfy Premium',
    title: 'Unlock Surfy Premium',
    body: 'Upgrade for full playback control, studio-quality sound, and the complete Surfy look.',
    benefits: [
      'Listen to any song you want (No forced shuffle)',
      'Studio Quality Audio with custom EQ & Bass Boost',
      'Unlimited skips',
      'Unlock all custom visual themes',
      'Create unlimited playlists and libraries',
      'Ad-free listening',
    ],
    subscribe: 'Subscribe Now',
  },
  layout: {
    support: 'Support',
    download: 'Download',
    login: 'Log in',
    signup: 'Sign up',
    brandColumn: 'Surfy',
    premiumSubscription: 'Premium subscription',
    appDownload: 'Download the app',
    contactSupport: 'Contact / Support',
    imprint: 'Imprint',
    company: 'Company',
    communities: 'Communities',
    usefulLinks: 'Useful links',
    plans: 'Surfy Plans',
    about: 'About',
    jobs: 'Jobs',
    news: 'For the Record',
    forArtists: 'For Artists',
    developers: 'Developers',
    advertising: 'Advertising',
    investors: 'Investors',
    vendors: 'Vendors',
    webPlayer: 'Web Player',
    mobileApp: 'Free Mobile App',
    individual: 'Premium Individual',
    duo: 'Premium Duo',
    family: 'Premium Family',
    student: 'Premium Student',
    legal: 'Legal',
    privacyCenter: 'Privacy Center',
    privacyPolicy: 'Privacy Policy',
    cookies: 'Cookies',
    aboutAds: 'About Ads',
    accessibility: 'Accessibility',
    terms: 'Terms',
    rights: '© 2026 Surfy',
  },
  premium: {
    eyebrow: 'Surfy Premium',
    heroTitle:
      'The ultimate home for music. Listen without limits with Surfy Premium.',
    heroBody:
      "Premium Individual only. Free for 1 month, then 990 Ft per month. Offer only available if you haven't tried Premium before.",
    termsApply: 'Terms apply.',
    viewPlans: 'View plans',
    tryFree: 'Try 1 month for $0',
    perks: [
      {
        title: 'Ad-free music listening',
        body: 'All the music, without all the ads. Just nonstop beats.',
      },
      {
        title: 'Studio quality sound',
        body: 'Lose yourself in music with high-fidelity audio, custom EQ, and Bass Boost.',
      },
      {
        title: 'Custom themes and backgrounds',
        body: 'Shape the interface to your taste! Choose from a wide range of special themes and backgrounds so the player looks exactly like your style.',
      },
      {
        title: 'Play songs in any order',
        body: 'Repeat songs, play albums in any order, and skip as many times as you want.',
      },
    ],
    difference: 'Experience the difference',
    whatYouGet: 'What you get',
    free: 'Surfy Free',
    premium: 'Surfy Premium',
    comparison: [
      'Ad-free music listening',
      'Play songs in any order',
      'Studio quality audio',
      'Unlimited skips',
      'Custom visual themes',
      'Unlimited playlists and libraries',
    ],
    plansTitle: 'Affordable plans for every kind of listener',
    plansBody:
      'Choose a Premium plan and listen to ad-free music on your phone, speaker, and other devices. Cancel anytime.',
    plansNote: 'All Premium plans include the core listening benefits below.',
    premiumTerms: 'Premium terms',
    mostPopular: 'Most popular',
    perMonth: '/ mo',
    getStarted: 'Get started',
    termsApplyShort: 'Terms apply',
    questions: 'Questions?',
    faqs: [
      {
        q: 'How does the Premium trial work?',
        a: 'If you have never tried Surfy Premium, you can start Individual or Student for $0 for 1 month. After that, you are billed the regular monthly price unless you cancel. Offer terms apply.',
      },
      {
        q: 'How do I cancel?',
        a: 'You can cancel anytime from your account page. Your Premium benefits stay active until the end of the current billing period.',
      },
      {
        q: 'How does the Family plan work?',
        a: 'Family includes up to 6 separate Premium accounts under one bill. Members must live at the same address. Each person keeps their own playlists and recommendations.',
      },
      {
        q: 'Who is eligible for Student?',
        a: 'Student is for eligible and accredited higher-education students. You will need to verify enrollment. Student terms apply.',
        studentTerms: true,
      },
      {
        q: 'Which payment methods can I use?',
        a: 'Available methods depend on your country. You can typically pay with a credit card, debit card, or other local options shown at checkout.',
      },
      {
        q: 'Can I change plans later?',
        a: 'Yes. You can switch between Individual, Student, Duo, and Family from your account as long as you meet each plan’s eligibility rules.',
      },
    ],
    readStudentTerms: 'Read student terms',
    stillNeedHelp: 'Still need help?',
    visitSupport: 'Visit Support',
    plans: [
      {
        id: 'individual',
        name: 'Individual',
        price: '990 Ft',
        trial: 'Free for 1 month',
        description: '1 Premium account for you.',
        features: [
          'Ad-free listening',
          'Play any song on demand',
          'Offline downloads',
          'Studio quality audio with EQ',
        ],
        to: '/premium/plans/individual',
        termsTo: '/premium/offer-terms',
        highlighted: true,
      },
      {
        id: 'student',
        name: 'Student',
        price: '$4.99',
        trial: '$0 for 1 month',
        description: 'Discounted Premium for eligible students.',
        features: [
          'Everything in Individual',
          'Verified student pricing',
          'Full playback control',
          'Unlock all visual themes',
        ],
        to: '/premium/plans/student',
        termsTo: '/premium/student-terms',
        highlighted: false,
      },
      {
        id: 'duo',
        name: 'Duo',
        price: '$12.99',
        trial: null,
        description: '2 Premium accounts for a couple who live together.',
        features: [
          '2 separate Premium accounts',
          'Ad-free listening for both',
          'Play any song on demand',
          'One shared bill',
        ],
        to: '/premium/plans/duo',
        termsTo: '/premium/terms',
        highlighted: false,
      },
      {
        id: 'family',
        name: 'Family',
        price: '$14.99',
        trial: null,
        description:
          'Up to 6 Premium accounts for family members who live together.',
        features: [
          'Up to 6 separate accounts',
          'Kids-friendly profiles',
          'Block explicit music',
          'One shared billing cycle',
        ],
        to: '/premium/plans/family',
        termsTo: '/premium/terms',
        highlighted: false,
      },
    ],
  },
  support: {
    eyebrow: 'Surfy Support',
    title: 'How can we help you?',
    searchLabel: 'Search help articles',
    searchPlaceholder: 'Search payments, plans, the app, and more',
    empty: 'No matching help topics. Try a different search.',
    back: 'Back to Support',
    articleTitle: 'Article',
    articleBody:
      'This is a placeholder article. The full help content will appear here.',
    articleMore:
      'Check back soon for step-by-step guidance, or return to Support to browse other topics.',
    categories: [
      {
        id: 'billing',
        title: 'Payments & billing',
        description: 'Payment methods, charges, receipts, and cancellations.',
        topics: [
          {id: 'price-updates', label: 'Price updates'},
          {id: 'failed-payment', label: 'Failed payment help'},
          {id: 'payment-methods', label: 'Accepted payment methods'},
          {id: 'change-payment', label: 'How to change your payment details'},
          {id: 'cancel-premium', label: 'How to cancel Premium plans'},
        ],
      },
      {
        id: 'account',
        title: 'Manage your account',
        description: 'Login, profile, security, and account settings.',
        topics: [
          {id: 'cant-log-in', label: 'Can’t log in to Surfy'},
          {id: 'reset-password', label: 'How to reset or change your Surfy password'},
          {id: 'customize-profile', label: 'Manage and customize your Surfy profile'},
          {id: 'change-email', label: 'Change your email address'},
          {id: 'protect-account', label: 'Protect your Surfy account'},
        ],
      },
      {
        id: 'plans',
        title: 'Premium and Artist accounts',
        description: 'Surfy Premium, Artist accounts, and switching account types.',
        topics: [
          {id: 'surfy-premium', label: 'Surfy Premium (Individual)'},
          {id: 'artist-account', label: 'Artist account'},
          {id: 'upload-music', label: 'Uploading your own music'},
          {id: 'free-vs-premium', label: 'Free vs. Premium'},
          {id: 'change-account-type', label: 'Changing your account type'},
        ],
      },
      {
        id: 'features',
        title: 'In-app features',
        description: 'Library, playlists, playback, and app settings.',
        topics: [
          {id: 'getting-started', label: 'Getting started on Surfy'},
          {id: 'playlists', label: 'Create and edit playlists'},
          {id: 'appearance', label: 'Appearance and custom themes'},
          {id: 'equalizer', label: 'Equalizer'},
          {id: 'web-player', label: 'Surfy web player help'},
        ],
      },
      {
        id: 'devices',
        title: 'Devices & troubleshooting',
        description: 'Speakers, TVs, cars, consoles, and connection help.',
        topics: [
          {id: 'speakers', label: 'Surfy on speakers'},
          {id: 'connect', label: 'Surfy Connect'},
          {id: 'bluetooth', label: 'Bluetooth help for Surfy'},
          {id: 'tv', label: 'Surfy on TV'},
          {id: 'car', label: 'Surfy in the car'},
        ],
      },
      {
        id: 'privacy',
        title: 'Safety & privacy',
        description: 'Data rights, privacy choices, and account protection.',
        topics: [
          {id: 'data-rights', label: 'Data rights and privacy choices'},
          {id: 'your-data', label: 'Understanding your data'},
          {id: 'privacy-centre', label: 'Safety and Privacy Centre'},
          {id: 'hacked', label: 'Think your account’s been hacked?'},
          {id: 'close-account', label: 'Closing your account and deleting your data'},
        ],
      },
    ],
  },
};

const hu: PremiumCopy = {
  modal: {
    trigger: 'Surfy Premium',
    title: 'Oldd fel a Surfy Premiumot',
    body: 'Válts a teljes lejátszásvezérlésért, stúdióminőségű hangért és a teljes Surfy megjelenésért.',
    benefits: [
      'Hallgass bármilyen dalt (nincs kényszerített keverés)',
      'Stúdióminőségű hang egyedi EQ-val és basszuserősítéssel',
      'Korlátlan ugrás',
      'Minden egyedi vizuális téma',
      'Korlátlan lejátszási lista és gyűjtemény',
      'Hirdetésmentes zenehallgatás',
    ],
    subscribe: 'Fizess elő most',
  },
  layout: {
    support: 'Támogatás',
    download: 'Letöltés',
    login: 'Bejelentkezés',
    signup: 'Regisztráció',
    brandColumn: 'Surfy',
    premiumSubscription: 'Premium előfizetés',
    appDownload: 'Alkalmazás letöltése',
    contactSupport: 'Kapcsolat / Támogatás',
    imprint: 'Impresszum',
    company: 'Cég',
    communities: 'Közösségek',
    usefulLinks: 'Hasznos linkek',
    plans: 'Surfy csomagok',
    about: 'Rólunk',
    jobs: 'Állások',
    news: 'Hírek',
    forArtists: 'Előadóknak',
    developers: 'Fejlesztőknek',
    advertising: 'Hirdetés',
    investors: 'Befektetőknek',
    vendors: 'Partnereknek',
    webPlayer: 'Webes lejátszó',
    mobileApp: 'Ingyenes mobilalkalmazás',
    individual: 'Premium Egyéni',
    duo: 'Premium Duo',
    family: 'Premium Családi',
    student: 'Premium Diák',
    legal: 'Jogi információk',
    privacyCenter: 'Adatvédelmi központ',
    privacyPolicy: 'Adatvédelmi irányelvek',
    cookies: 'Sütik',
    aboutAds: 'A hirdetésekről',
    accessibility: 'Akadálymentesség',
    terms: 'ÁSZF',
    rights: '© 2026 Surfy',
  },
  premium: {
    eyebrow: 'Surfy Premium',
    heroTitle:
      'A zene igazi otthona. Hallgass korlátok nélkül a Surfy Premiummal.',
    heroBody:
      'Csak Premium Egyéni. Egy hónap ingyenes, utána havi 990 forint. Az ajánlat csak akkor érvényes, ha még nem próbáltad a Premiumot.',
    termsApply: 'A feltételek érvényesek.',
    viewPlans: 'Csomagok megtekintése',
    tryFree: 'Próbáld ki 1 hónapig 0 dollárért',
    perks: [
      {
        title: 'Hirdetésmentes zenehallgatás',
        body: 'Minden zene, hirdetések nélkül. Csak a ritmus.',
      },
      {
        title: 'Stúdióminőségű hang',
        body: 'Merülj el a zenében nagy felbontású hanggal, egyedi EQ-val és basszuserősítéssel.',
      },
      {
        title: 'Egyedi témák és hátterek',
        body: 'Alakítsd a felületet a saját ízlésedre! Rengeteg különleges téma és háttér közül választhatsz, hogy a lejátszó megjelenése tökéletesen illeszkedjen a te stílusodhoz.',
      },
      {
        title: 'Dalok tetszőleges sorrendben',
        body: 'Ismételj dalokat, játssz lemezed bármilyen sorrendben, és ugorj, ahányszor csak szeretnél.',
      },
    ],
    difference: 'Érezd a különbséget',
    whatYouGet: 'Amit kapsz',
    free: 'Surfy Free',
    premium: 'Surfy Premium',
    comparison: [
      'Hirdetésmentes zenehallgatás',
      'Dalok tetszőleges sorrendben',
      'Stúdióminőségű hang',
      'Korlátlan ugrás',
      'Egyedi vizuális témák',
      'Korlátlan lejátszási lista és gyűjtemény',
    ],
    plansTitle: 'Megfizethető csomagok minden hallgatónak',
    plansBody:
      'Válassz Premium csomagot, és hallgass hirdetésmentes zenét a telefonodon, hangszóródon és más eszközeiden. Bármikor lemondhatod.',
    plansNote: 'Minden Premium csomag tartalmazza az alábbi alapvető előnyöket.',
    premiumTerms: 'Premium feltételek',
    mostPopular: 'Legnépszerűbb',
    perMonth: '/ hó',
    getStarted: 'Kezdd el',
    termsApplyShort: 'A feltételek érvényesek',
    questions: 'Kérdések?',
    faqs: [
      {
        q: 'Hogyan működik a Premium próbaidő?',
        a: 'Ha még nem próbáltad a Surfy Premiumot, az Egyéni vagy Diák csomagot 1 hónapig 0 dollárért indíthatod. Utána a szokásos havi díjat számlázzuk, hacsak nem mondod le. Az ajánlat feltételei érvényesek.',
      },
      {
        q: 'Hogyan mondhatom le?',
        a: 'Bármikor lemondhatod a fiókod oldalán. A Premium előnyei a számlázási időszak végéig megmaradnak.',
      },
      {
        q: 'Hogyan működik a Családi csomag?',
        a: 'A Családi csomag legfeljebb 6 külön Premium fiókot tartalmaz egy számlán. A tagoknak ugyanazon a címen kell lakniuk. Mindenkinek saját lejátszási listái és ajánlásai maradnak.',
      },
      {
        q: 'Ki jogosult a Diák csomagra?',
        a: 'A Diák csomag jogosult, akkreditált felsőoktatási hallgatóknak szól. Igazolnod kell a hallgatói jogviszonyt. A diákfeltételek érvényesek.',
        studentTerms: true,
      },
      {
        q: 'Milyen fizetési módokat használhatok?',
        a: 'Az elérhető módok az országodtól függnek. Általában bankkártyával vagy a fizetésnél megjelenő helyi lehetőségekkel fizethetsz.',
      },
      {
        q: 'Később válthatok csomagot?',
        a: 'Igen. A fiókodban válthatsz az Egyéni, Diák, Duo és Családi csomag között, ha megfelelsz az adott csomag feltételeinek.',
      },
    ],
    readStudentTerms: 'Diákfeltételek elolvasása',
    stillNeedHelp: 'Még mindig segítségre van szükséged?',
    visitSupport: 'Ugrás a támogatáshoz',
    plans: [
      {
        id: 'individual',
        name: 'Egyéni',
        price: '990 Ft',
        trial: '1 hónap ingyenes',
        description: '1 Premium fiók neked.',
        features: [
          'Hirdetésmentes hallgatás',
          'Bármely dal kérésre',
          'Offline letöltések',
          'Stúdióminőségű hang EQ-val',
        ],
        to: '/premium/plans/individual',
        termsTo: '/premium/offer-terms',
        highlighted: true,
      },
      {
        id: 'student',
        name: 'Diák',
        price: '$4.99',
        trial: '1 hónap 0 dollárért',
        description: 'Kedvezményes Premium jogosult hallgatóknak.',
        features: [
          'Minden, ami az Egyéniben van',
          'Igazolt diákár',
          'Teljes lejátszásvezérlés',
          'Minden vizuális téma',
        ],
        to: '/premium/plans/student',
        termsTo: '/premium/student-terms',
        highlighted: false,
      },
      {
        id: 'duo',
        name: 'Duo',
        price: '$12.99',
        trial: null,
        description: '2 Premium fiók egy együtt élő párnak.',
        features: [
          '2 külön Premium fiók',
          'Hirdetésmentes hallgatás mindkettőtöknek',
          'Bármely dal kérésre',
          'Egy közös számla',
        ],
        to: '/premium/plans/duo',
        termsTo: '/premium/terms',
        highlighted: false,
      },
      {
        id: 'family',
        name: 'Családi',
        price: '$14.99',
        trial: null,
        description: 'Legfeljebb 6 Premium fiók együtt élő családtagoknak.',
        features: [
          'Legfeljebb 6 külön fiók',
          'Gyerekbarát profilok',
          'Explicit zene tiltása',
          'Egy közös számlázási ciklus',
        ],
        to: '/premium/plans/family',
        termsTo: '/premium/terms',
        highlighted: false,
      },
    ],
  },
  support: {
    eyebrow: 'Surfy támogatás',
    title: 'Miben segíthetünk?',
    searchLabel: 'Súgócikkek keresése',
    searchPlaceholder: 'Keresés fizetés, csomagok, alkalmazás és egyebek között',
    empty: 'Nincs találat. Próbálj másik keresést.',
    back: 'Vissza a támogatáshoz',
    articleTitle: 'Cikk',
    articleBody:
      'Ez egy helyőrző cikk. A teljes súgószöveg itt fog megjelenni.',
    articleMore:
      'Hamarosan lépésről lépésre útmutatót találsz itt, vagy térj vissza a támogatáshoz más témákért.',
    categories: [
      {
        id: 'billing',
        title: 'Fizetés és számlázás',
        description: 'Fizetési módok, terhelések, nyugták és lemondás.',
        topics: [
          {id: 'price-updates', label: 'Árváltozások'},
          {id: 'failed-payment', label: 'Sikertelen fizetés'},
          {id: 'payment-methods', label: 'Elfogadott fizetési módok'},
          {id: 'change-payment', label: 'Fizetési adatok módosítása'},
          {id: 'cancel-premium', label: 'Premium csomag lemondása'},
        ],
      },
      {
        id: 'account',
        title: 'Fiók kezelése',
        description: 'Bejelentkezés, profil, biztonság és fiókbeállítások.',
        topics: [
          {id: 'cant-log-in', label: 'Nem tudok belépni a Surfyba'},
          {id: 'reset-password', label: 'Surfy jelszó visszaállítása vagy módosítása'},
          {id: 'customize-profile', label: 'Surfy profil kezelése és testreszabása'},
          {id: 'change-email', label: 'E-mail-cím módosítása'},
          {id: 'protect-account', label: 'Surfy fiók védelme'},
        ],
      },
      {
        id: 'plans',
        title: 'Premium és Előadói fiókok',
        description: 'Surfy Premium, Előadói (Artist) fiókok és csomagváltás.',
        topics: [
          {id: 'surfy-premium', label: 'Surfy Premium (Egyéni)'},
          {id: 'artist-account', label: 'Előadói fiók (Artist)'},
          {id: 'upload-music', label: 'Saját zenék feltöltése'},
          {id: 'free-vs-premium', label: 'Ingyenes vs. Premium'},
          {id: 'change-account-type', label: 'Fióktípus módosítása'},
        ],
      },
      {
        id: 'features',
        title: 'Alkalmazáson belüli funkciók',
        description: 'Gyűjtemény, lejátszási listák, lejátszás és beállítások.',
        topics: [
          {id: 'getting-started', label: 'Kezdő lépések a Surfyban'},
          {id: 'playlists', label: 'Lejátszási listák létrehozása és szerkesztése'},
          {id: 'appearance', label: 'Megjelenés és egyedi témák'},
          {id: 'equalizer', label: 'Hangszínszabályzó'},
          {id: 'web-player', label: 'Surfy webes lejátszó súgó'},
        ],
      },
      {
        id: 'devices',
        title: 'Eszközök és hibaelhárítás',
        description: 'Hangszórók, tévék, autók, konzolok és kapcsolat.',
        topics: [
          {id: 'speakers', label: 'Surfy hangszórókon'},
          {id: 'connect', label: 'Surfy Connect'},
          {id: 'bluetooth', label: 'Bluetooth súgó a Surfyhoz'},
          {id: 'tv', label: 'Surfy a tévén'},
          {id: 'car', label: 'Surfy az autóban'},
        ],
      },
      {
        id: 'privacy',
        title: 'Biztonság és adatvédelem',
        description: 'Adatjogok, adatvédelmi választások és fiókvédelem.',
        topics: [
          {id: 'data-rights', label: 'Adatjogok és adatvédelmi választások'},
          {id: 'your-data', label: 'Az adataid megértése'},
          {id: 'privacy-centre', label: 'Biztonsági és adatvédelmi központ'},
          {id: 'hacked', label: 'Úgy gondolod, feltörték a fiókodat?'},
          {id: 'close-account', label: 'Fiók lezárása és az adatok törlése'},
        ],
      },
    ],
  },
};

const de: PremiumCopy = {
  modal: {
    trigger: 'Surfy Premium',
    title: 'Surfy Premium freischalten',
    body: 'Wechsle für volle Wiedergabesteuerung, Studio-Klang und das komplette Surfy-Erscheinungsbild.',
    benefits: [
      'Höre jeden Song (kein erzwungenes Mischen)',
      'Studioqualität mit eigenem EQ und Bass-Boost',
      'Unbegrenztes Überspringen',
      'Alle eigenen visuellen Designs',
      'Unbegrenzt Playlists und Bibliotheken',
      'Werbefreies Hören',
    ],
    subscribe: 'Jetzt abonnieren',
  },
  layout: {
    support: 'Support',
    download: 'Download',
    login: 'Anmelden',
    signup: 'Registrieren',
    brandColumn: 'Surfy',
    premiumSubscription: 'Premium-Abo',
    appDownload: 'App herunterladen',
    contactSupport: 'Kontakt / Support',
    imprint: 'Impressum',
    company: 'Unternehmen',
    communities: 'Communitys',
    usefulLinks: 'Nützliche Links',
    plans: 'Surfy-Tarife',
    about: 'Über uns',
    jobs: 'Jobs',
    news: 'Aktuelles',
    forArtists: 'Für Künstler',
    developers: 'Entwickler',
    advertising: 'Werbung',
    investors: 'Investoren',
    vendors: 'Partner',
    webPlayer: 'Web-Player',
    mobileApp: 'Kostenlose Mobile-App',
    individual: 'Premium Einzel',
    duo: 'Premium Duo',
    family: 'Premium Familie',
    student: 'Premium Student',
    legal: 'Rechtliches',
    privacyCenter: 'Datenschutzcenter',
    privacyPolicy: 'Datenschutzrichtlinie',
    cookies: 'Cookies',
    aboutAds: 'Über Werbung',
    accessibility: 'Barrierefreiheit',
    terms: 'Bedingungen',
    rights: '© 2026 Surfy',
  },
  premium: {
    eyebrow: 'Surfy Premium',
    heroTitle:
      'Das Zuhause für Musik. Höre ohne Grenzen mit Surfy Premium.',
    heroBody:
      'Nur Premium Einzel. 1 Monat kostenlos, danach 990 Ft pro Monat. Das Angebot gilt nur, wenn du Premium noch nicht getestet hast.',
    termsApply: 'Es gelten Bedingungen.',
    viewPlans: 'Tarife ansehen',
    tryFree: '1 Monat für 0 $ testen',
    perks: [
      {
        title: 'Werbefreie Musik',
        body: 'Die ganze Musik, ohne die Werbung. Nur der Beat.',
      },
      {
        title: 'Klang in Studioqualität',
        body: 'Tauche ein mit High-Fidelity-Audio, eigenem EQ und Bass-Boost.',
      },
      {
        title: 'Individuelle Themes und Hintergründe',
        body: 'Gestalte die Oberfläche nach deinem Geschmack! Wähle aus vielen besonderen Themes und Hintergründen, damit der Player perfekt zu deinem Stil passt.',
      },
      {
        title: 'Songs in jeder Reihenfolge',
        body: 'Wiederhole Songs, spiele Alben in beliebiger Reihenfolge und überspringe so oft du willst.',
      },
    ],
    difference: 'Erlebe den Unterschied',
    whatYouGet: 'Das bekommst du',
    free: 'Surfy Free',
    premium: 'Surfy Premium',
    comparison: [
      'Werbefreie Musik',
      'Songs in jeder Reihenfolge',
      'Audio in Studioqualität',
      'Unbegrenztes Überspringen',
      'Eigene visuelle Designs',
      'Unbegrenzt Playlists und Bibliotheken',
    ],
    plansTitle: 'Günstige Tarife für jede Art von Hörer',
    plansBody:
      'Wähle einen Premium-Tarif und höre werbefreie Musik auf Handy, Lautsprecher und anderen Geräten. Jederzeit kündbar.',
    plansNote: 'Alle Premium-Tarife enthalten die folgenden Kernvorteile.',
    premiumTerms: 'Premium-Bedingungen',
    mostPopular: 'Am beliebtesten',
    perMonth: '/ Mon.',
    getStarted: 'Loslegen',
    termsApplyShort: 'Es gelten Bedingungen',
    questions: 'Fragen?',
    faqs: [
      {
        q: 'Wie funktioniert die Premium-Testphase?',
        a: 'Wenn du Surfy Premium noch nie getestet hast, kannst du Einzel oder Student 1 Monat für 0 $ starten. Danach gilt der normale Monatspreis, sofern du nicht kündigst. Die Angebotsbedingungen gelten.',
      },
      {
        q: 'Wie kündige ich?',
        a: 'Du kannst jederzeit auf deiner Kontoseite kündigen. Deine Premium-Vorteile bleiben bis zum Ende des aktuellen Abrechnungszeitraums aktiv.',
      },
      {
        q: 'Wie funktioniert der Familientarif?',
        a: 'Familie umfasst bis zu 6 separate Premium-Konten auf einer Rechnung. Die Mitglieder müssen an derselben Adresse wohnen. Jede Person behält eigene Playlists und Empfehlungen.',
      },
      {
        q: 'Wer ist für Student berechtigt?',
        a: 'Student ist für berechtigte Studierende an anerkannten Hochschulen. Du musst deine Immatrikulation nachweisen. Die Studentenbedingungen gelten.',
        studentTerms: true,
      },
      {
        q: 'Welche Zahlungsmethoden kann ich nutzen?',
        a: 'Die verfügbaren Methoden hängen von deinem Land ab. In der Regel zahlst du mit Kreditkarte, Debitkarte oder anderen lokalen Optionen an der Kasse.',
      },
      {
        q: 'Kann ich den Tarif später wechseln?',
        a: 'Ja. Du kannst in deinem Konto zwischen Einzel, Student, Duo und Familie wechseln, solange du die Voraussetzungen des Tarifs erfüllst.',
      },
    ],
    readStudentTerms: 'Studentenbedingungen lesen',
    stillNeedHelp: 'Brauchst du noch Hilfe?',
    visitSupport: 'Zum Support',
    plans: [
      {
        id: 'individual',
        name: 'Einzel',
        price: '990 Ft',
        trial: '1 Monat kostenlos',
        description: '1 Premium-Konto für dich.',
        features: [
          'Werbefreies Hören',
          'Jeden Song auf Abruf',
          'Offline-Downloads',
          'Studioqualität mit EQ',
        ],
        to: '/premium/plans/individual',
        termsTo: '/premium/offer-terms',
        highlighted: true,
      },
      {
        id: 'student',
        name: 'Student',
        price: '$4.99',
        trial: '1 Monat für 0 $',
        description: 'Ermäßigtes Premium für berechtigte Studierende.',
        features: [
          'Alles aus Einzel',
          'Verifizierter Studentenpreis',
          'Volle Wiedergabesteuerung',
          'Alle visuellen Designs',
        ],
        to: '/premium/plans/student',
        termsTo: '/premium/student-terms',
        highlighted: false,
      },
      {
        id: 'duo',
        name: 'Duo',
        price: '$12.99',
        trial: null,
        description: '2 Premium-Konten für ein Paar, das zusammenwohnt.',
        features: [
          '2 separate Premium-Konten',
          'Werbefreies Hören für beide',
          'Jeden Song auf Abruf',
          'Eine gemeinsame Rechnung',
        ],
        to: '/premium/plans/duo',
        termsTo: '/premium/terms',
        highlighted: false,
      },
      {
        id: 'family',
        name: 'Familie',
        price: '$14.99',
        trial: null,
        description:
          'Bis zu 6 Premium-Konten für Familienmitglieder, die zusammenwohnen.',
        features: [
          'Bis zu 6 separate Konten',
          'Kinderfreundliche Profile',
          'Explizite Musik sperren',
          'Ein gemeinsamer Abrechnungszeitraum',
        ],
        to: '/premium/plans/family',
        termsTo: '/premium/terms',
        highlighted: false,
      },
    ],
  },
  support: {
    eyebrow: 'Surfy Support',
    title: 'Wie können wir helfen?',
    searchLabel: 'Hilfeartikel durchsuchen',
    searchPlaceholder: 'Suche nach Zahlung, Tarifen, der App und mehr',
    empty: 'Keine passenden Themen. Versuche eine andere Suche.',
    back: 'Zurück zum Support',
    articleTitle: 'Artikel',
    articleBody:
      'Dies ist ein Platzhalter-Artikel. Der vollständige Hilfetext erscheint hier.',
    articleMore:
      'Schau bald wieder vorbei, oder kehre zum Support zurück, um andere Themen zu sehen.',
    categories: [
      {
        id: 'billing',
        title: 'Zahlung und Abrechnung',
        description: 'Zahlungsmethoden, Abbuchungen, Belege und Kündigung.',
        topics: [
          {id: 'price-updates', label: 'Preisänderungen'},
          {id: 'failed-payment', label: 'Hilfe bei fehlgeschlagener Zahlung'},
          {id: 'payment-methods', label: 'Akzeptierte Zahlungsmethoden'},
          {id: 'change-payment', label: 'Zahlungsdaten ändern'},
          {id: 'cancel-premium', label: 'Premium-Tarife kündigen'},
        ],
      },
      {
        id: 'account',
        title: 'Konto verwalten',
        description: 'Anmeldung, Profil, Sicherheit und Kontoeinstellungen.',
        topics: [
          {id: 'cant-log-in', label: 'Anmeldung bei Surfy klappt nicht'},
          {id: 'reset-password', label: 'Surfy-Passwort zurücksetzen oder ändern'},
          {id: 'customize-profile', label: 'Surfy-Profil verwalten und anpassen'},
          {id: 'change-email', label: 'E-Mail-Adresse ändern'},
          {id: 'protect-account', label: 'Surfy-Konto schützen'},
        ],
      },
      {
        id: 'plans',
        title: 'Premium- und Künstlerkonten',
        description: 'Surfy Premium, Künstlerkonten (Artist) und Kontowechsel.',
        topics: [
          {id: 'surfy-premium', label: 'Surfy Premium (Einzel)'},
          {id: 'artist-account', label: 'Künstlerkonto (Artist)'},
          {id: 'upload-music', label: 'Eigene Musik hochladen'},
          {id: 'free-vs-premium', label: 'Kostenlos vs. Premium'},
          {id: 'change-account-type', label: 'Kontotyp ändern'},
        ],
      },
      {
        id: 'features',
        title: 'Funktionen in der App',
        description: 'Bibliothek, Playlists, Wiedergabe und Einstellungen.',
        topics: [
          {id: 'getting-started', label: 'Erste Schritte mit Surfy'},
          {id: 'playlists', label: 'Playlists erstellen und bearbeiten'},
          {id: 'appearance', label: 'Erscheinungsbild und individuelle Themes'},
          {id: 'equalizer', label: 'Equalizer'},
          {id: 'web-player', label: 'Hilfe zum Surfy-Web-Player'},
        ],
      },
      {
        id: 'devices',
        title: 'Geräte und Fehlerbehebung',
        description: 'Lautsprecher, Fernseher, Autos, Konsolen und Verbindung.',
        topics: [
          {id: 'speakers', label: 'Surfy auf Lautsprechern'},
          {id: 'connect', label: 'Surfy Connect'},
          {id: 'bluetooth', label: 'Bluetooth-Hilfe für Surfy'},
          {id: 'tv', label: 'Surfy auf dem Fernseher'},
          {id: 'car', label: 'Surfy im Auto'},
        ],
      },
      {
        id: 'privacy',
        title: 'Sicherheit und Datenschutz',
        description: 'Datenrechte, Datenschutzoptionen und Kontoschutz.',
        topics: [
          {id: 'data-rights', label: 'Datenrechte und Datenschutzoptionen'},
          {id: 'your-data', label: 'Deine Daten verstehen'},
          {id: 'privacy-centre', label: 'Sicherheits- und Datenschutzcenter'},
          {id: 'hacked', label: 'Glaubst du, dein Konto wurde gehackt?'},
          {id: 'close-account', label: 'Konto schließen und Daten löschen'},
        ],
      },
    ],
  },
};

export const premiumDictionary: Record<PremiumLocale, PremiumCopy> = {
  en,
  hu,
  de,
};

export function toPremiumLocale(value?: string | null): PremiumLocale | null {
  if (!value) return null;
  const code = value.toLowerCase().replace('_', '-').split('-')[0];
  if (code === 'en' || code === 'hu' || code === 'de') {
    return code;
  }
  return null;
}

export function resolvePremiumLocale(
  param?: string | null,
  active?: string | null,
): PremiumLocale {
  return toPremiumLocale(param) ?? toPremiumLocale(active) ?? 'en';
}

export function premiumPath(lang: PremiumLocale): string {
  return `/premium/${lang}`;
}

export function supportPath(lang: PremiumLocale): string {
  return `/support/${lang}`;
}

export function usePremiumLocale() {
  const {lang: langParam} = useParams();
  const {localeCode} = useSelectedLocale();
  const lang = resolvePremiumLocale(langParam, localeCode);

  return {
    lang,
    copy: premiumDictionary[lang],
  };
}
